import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RiMailSendLine, RiCheckboxCircleLine, RiErrorWarningLine, RiTimeLine, RiArrowRightLine } from 'react-icons/ri';
import { Link, useHistory } from 'react-router-dom';
import api from '../services/api';
import '../css/financeiro-parcela-pagar.css';

/**
 * Estado da fila de cartas de um evento, para as telas marcarem o que está por sair.
 *
 * As cartas deixaram de sair no clique: o operador enfileira e um agendador externo
 * manda aos poucos. Sem sinal na tela, ele clica de novo — que foi como 126 cartas
 * saíram duplicadas em 13/08. Estes dois exportes são esse sinal.
 *
 * O dado vem do endpoint de progresso, e não da listagem de participantes, porque a
 * tela já o consulta para o card de andamento e a alternativa seria um join a mais
 * numa consulta paginada sobre uma tabela de ~190 colunas.
 */

const INTERVALO_POLLING_MS = 10000;

/**
 * Quem enxerga a fila. Espelha `PERFIS_FILA` do CartaFilaController — o back recusa o
 * `progresso` para os demais, entao consultar por eles e requisicao jogada fora.
 *
 * Exportada porque a lista aparecia copiada em quatro lugares (rota, hook, card e o
 * `perfisCarta` do painel): quem seguisse o docblock do back atualizaria dois e deixaria
 * dois para tras, sem nada acusar. O botao "Fila de envio" continua com lista propria, e
 * essa divergencia e deliberada.
 */
export const PERFIS_FILA = ['admin', 'diretor', 'gerenteprojeto', 'rsvp', 'operadorrsvp', 'gerentersvp', 'coordenadorrsvp'];

/**
 * Quem dispara carta em massa, e por isso abre a fila de todos os eventos (/cartaFila sem id).
 * Espelha `EventoRsvpPermissaoService::perfilPodeEnviarEmMassa`, que e a trava do back.
 *
 * NAO e o PERFIS_FILA: sem o `operadorrsvp`, que envia um a um e acompanha a fila pela tela do
 * evento. O `perfisEnviarMassa` do PainelEventoParticipante ainda e uma copia desta lista.
 */
export const PERFIS_ENVIO_MASSA = ['admin', 'diretor', 'gerenteprojeto', 'rsvp', 'gerentersvp', 'coordenadorrsvp'];

const ROTULO_STATUS = {
    pendente: 'Na fila',
    processando: 'Enviando',
    falhou: 'Falhou',
};

// Rotulos por escopo. Mapa e nao ternario porque sao TRES valores: com ternario, tudo
// que nao fosse 'processo' caia num texto de "no evento" que mentia sobre o recorte.
const ROTULO_TOTAL = {
    processo: 'Envio em andamento',
    filtrado: 'Total do recorte',
    vazio: 'Total',
};

const SUB_TOTAL = {
    processo: 'cartas neste envio',
    filtrado: 'cartas no recorte filtrado',
    vazio: 'nenhuma carta na fila',
};

const SUB_ENVIADAS = {
    processo: 'já saíram neste envio',
    filtrado: 'no recorte filtrado',
    vazio: 'nada na fila agora',
};

const SUB_SEM_FALHA = {
    processo: 'nenhuma neste envio',
    filtrado: 'nenhuma no recorte',
    vazio: 'nenhuma',
};

const ROTULO_TIPO = {
    convite: 'convite',
    informativa: 'informativa',
    certificado: 'certificado',
};

/**
 * Lê a fila do evento e mantém atualizado enquanto houver carta por sair.
 *
 * Devolve `porParticipante` como um objeto id → lista de {tipo, status, posicao}, que é a forma
 * que o back manda, para a tela consultar por id sem varrer array.
 */
export function useCartaFila(idEvento, token) {
    // ritmo, naFrente, eventosNaFrente e comecou alimentam a previsao do card do painel. Sem eles
    // o card dizia "comeca no proximo ciclo" ate com outros eventos na frente ou com o envio ja
    // em andamento — a tela de processo acertava e o painel nao.
    const [fila, setFila] = useState({ emAndamento: false, naFila: 0, totais: null, escopo: 'vazio', falhasNoProcesso: 0, porParticipante: {}, ritmo: null, naFrente: 0, eventosNaFrente: 0, comecou: false });

    // Cancela a consulta anterior antes de abrir outra, como nas demais telas de painel:
    // sem isso uma resposta lenta sobrescreve a nova e a tela anda para trás.
    const controllerRef = useRef(null);
    // Consulta em voo e desde quando: e o que deixa o tick do polling esperar a resposta em vez
    // de cancela-la. Ver o comentario no inicio de `buscar`.
    const emVooRef = useRef(false);
    const emVooDesdeRef = useRef(0);
    const timer = useRef(null);

    // Pausa o laco depois de erros seguidos, como faz a tela de fila. Sem isto o hook seguia
    // consultando de 10 em 10 segundos para sempre — sustentando o proprio 429 — desde que se
    // parou de zerar `emAndamento` no catch. O card continua visivel com os ultimos numeros
    // bons; o que para e so a consulta.
    const errosSeguidos = useRef(0);
    const retomada = useRef(null);
    const [pausado, setPausado] = useState(false);

    // Pausa com volta. Desligar de vez deixava o card exibindo "40 aguardando envio" pelo
    // resto da sessao depois de um cold start de 30s — e o unico jeito de sair disso era
    // disparar outra carta, que e o duplo disparo que a fila existe para impedir.
    const pausarComRetomada = useCallback(() => {
        setPausado(true);

        if (retomada.current) clearTimeout(retomada.current);

        retomada.current = setTimeout(() => {
            errosSeguidos.current = 0;
            setPausado(false);
        }, 60000);
    }, []);

    const buscar = useCallback((opcoes) => {
        if (!idEvento) return;

        // O tick do polling NAO atropela uma consulta em voo. Com o axios 0.27, que le `signal`,
        // abortar a cada tick fazia um `progresso` mais lento que o intervalo nunca voltar: a
        // tela parava de atualizar sem aviso, porque cancelamento nao conta como erro. Quem
        // muda o recorte chama sem opcao e aborta — ai a consulta anterior e que esta velha.
        //
        // O teto de idade existe porque a requisicao nao tem timeout: sem ele, uma consulta
        // presa para sempre faria o polling pular para sempre.
        if (opcoes && opcoes.doPolling && emVooRef.current
            && Date.now() - emVooDesdeRef.current < INTERVALO_POLLING_MS * 3) {
            return;
        }

        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        emVooRef.current = true;
        emVooDesdeRef.current = Date.now();

        api({
            method: 'get',
            action: `/carta/fila/progresso?evento=${idEvento}`,
            token: token,
            signal: controllerRef.current.signal,
        })
            .then(function (response) {
                emVooRef.current = false;
                const dados = response.data.data || {};

                // Recusa do back (token expirado, evento ausente) vem 200 com `mensagem` e
                // tudo zerado. Gravar isso apagaria o card e faria a tela de cadastro anunciar
                // "a carta saiu da fila" para uma carta ainda pendente. Mantem o ultimo estado
                // bom em vez de inventar um desfecho.
                if (dados.mensagem) {
                    errosSeguidos.current = 0;
                    // Terminal, como no PainelCartaFila: a recusa nao se resolve sozinha, e sem
                    // pausar o hook seguia consultando de 10 em 10 segundos para sempre — por aba
                    // aberta — alimentando o proprio 429. O card fica com os ultimos numeros bons.
                    pausarComRetomada();

                    return;
                }

                errosSeguidos.current = 0;

                setFila({
                    emAndamento: Boolean(dados.emAndamento),
                    naFila: dados.naFila || 0,
                    totais: dados.totais || null,
                    falhasNoProcesso: dados.falhasNoProcesso || 0,
                    escopo: dados.escopo || 'vazio',
                    porParticipante: dados.naFilaPorParticipante || {},
                    ritmo: dados.ritmo || null,
                    naFrente: dados.naFrente || 0,
                    eventosNaFrente: dados.eventosNaFrente || 0,
                    comecou: Boolean(dados.comecou),
                });
            })
            .catch(function (err) {
                // Cancelamento e substituicao, nao falha: sair sem tocar no estado.
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;

                emVooRef.current = false;

                // Silencioso de propósito: isto é um enfeite de status. Falhar aqui não pode
                // encher a tela de toast vermelho nem atrapalhar o trabalho.
                //
                // E NAO mexe em `emAndamento`. Zerar ali parava o polling — o que ate se
                // queria, para nao martelar servidor fora do ar — mas junto apagava o card,
                // que e o aviso contra o duplo clique. Um 502 de cold start no meio de um lote
                // sumia com ele ate alguem recarregar a pagina. Quem para o laco agora e
                // `pausado`, e o card fica onde esta.
                errosSeguidos.current += 1;

                if (errosSeguidos.current >= 3) {
                    pausarComRetomada();
                }
            });
    }, [idEvento, token]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    // Aborta o que estiver em voo ao desmontar: sem isto o `.then` acorda um componente que
    // ja saiu da tela.
    useEffect(() => () => {
        if (controllerRef.current) controllerRef.current.abort();
    }, []);

    // O timer da retomada precisa morrer com a tela: sem isto ele acorda um componente
    // desmontado e o React acusa setState em nada.
    useEffect(() => () => {
        if (retomada.current) clearTimeout(retomada.current);
    }, []);

    useEffect(() => {
        // Só consulta em laço enquanto há carta na fila. Esvaziou ou o servidor parou de
        // responder, para de pedir.
        if (!fila.emAndamento || pausado) {
            if (timer.current) {
                clearInterval(timer.current);
                timer.current = null;
            }
            return;
        }

        timer.current = setInterval(() => buscar({ doPolling: true }), INTERVALO_POLLING_MS);

        return () => {
            if (timer.current) {
                clearInterval(timer.current);
                timer.current = null;
            }
        };
    }, [fila.emAndamento, pausado, buscar]);

    return {
        fila,
        // Religa o laco: quem chama isto acabou de agir na tela, entao vale tentar de novo
        // mesmo depois de a pausa ter desistido.
        recarregarFila: () => {
            errosSeguidos.current = 0;
            setPausado(false);
            buscar();
        },
    };
}

/**
 * A marca de "esta carta ainda vai sair".
 *
 * `variante` muda só o tamanho: `linha` para dentro da célula da tabela, `cabecalho`
 * para o topo da tela de cadastro.
 *
 * `ritmo` liga a previsão na carta parada na fila ("Na fila: convite · ~3 min"), pela
 * `posicao` que o back manda em cada entrada. Sem ele a marca sai como antes.
 */
export function BadgeCartaFila({ entradas, variante = 'linha', ritmo }) {
    if (!entradas || entradas.length === 0) return null;

    const classe = variante === 'cabecalho' ? 'fin-pp__pill' : 'fin-pp__minitag';
    const tamanho = variante === 'cabecalho' ? 13 : 11;

    // Falha e fila sao marcas separadas, nunca fundidas: pintar de laranja "na fila" uma
    // carta que falhou faria o operador esperar por um envio que nunca vai acontecer —
    // `falhou` e terminal, o reenvio e ato dele.
    // Tres marcas, uma por situacao. Fundir pendente e processando num "Enviando" so dizia
    // que um convite ainda parado na fila estava saindo — o mesmo motivo pelo qual a falha ja
    // tinha marca propria.
    const pendentes = entradas.filter((e) => e.status === 'pendente');
    const saindo = entradas.filter((e) => e.status === 'processando');
    const falharam = entradas.filter((e) => e.status === 'falhou');

    // Um participante pode ter convite e informativa ao mesmo tempo — o indice unico e por
    // participante E tipo. Uma marca por situacao, listando os tipos.
    const rotulo = (linhas, status) =>
        `${ROTULO_STATUS[status]}: ${linhas.map((e) => ROTULO_TIPO[e.tipo] || e.tipo).join(', ')}`;

    // Previsao por tipo, e nao uma so para a marca: convite e informativa do mesmo participante
    // podem estar em lotes diferentes, e um tempo unico diria a hora de uma delas como se fosse
    // das duas. So no pendente — a que esta `processando` ja esta saindo.
    const rotuloPendentes = (linhas) =>
        `${ROTULO_STATUS.pendente}: ${linhas.map((e) => {
            const tipo = ROTULO_TIPO[e.tipo] || e.tipo;
            const quando = ritmo ? estimativaFila(e.posicao, ritmo) : null;

            return quando ? `${tipo} · ${quando}` : tipo;
        }).join(', ')}`;

    return (
        <>
            {saindo.length > 0 ? (
                <span
                    className={`${classe} is-info`}
                    title="A carta está sendo enviada neste momento">
                    <RiMailSendLine size={tamanho} /> {rotulo(saindo, 'processando')}
                </span>
            ) : null}
            {pendentes.length > 0 ? (
                <span
                    className={`${classe} is-warn`}
                    style={saindo.length > 0 ? { marginLeft: 6 } : null}
                    title="A carta foi solicitada e está aguardando envio">
                    <RiMailSendLine size={tamanho} /> {rotuloPendentes(pendentes)}
                </span>
            ) : null}
            {falharam.length > 0 ? (
                <span
                    className={`${classe} is-danger`}
                    style={(saindo.length + pendentes.length) > 0 ? { marginLeft: 6 } : null}
                    title="A última tentativa de envio falhou — veja o motivo na fila de envio">
                    <RiErrorWarningLine size={tamanho} /> {rotulo(falharam, 'falhou')}
                </span>
            ) : null}
        </>
    );
}

/**
 * Os quatro numeros da fila, no mesmo desenho no painel e na tela de processo.
 *
 * `idEvento` transforma o ultimo card em link para a tela de processo. Na propria
 * tela de processo ele vem vazio: card que leva para onde o operador ja esta e so
 * ruido.
 *
 * `escopo` diz o que os numeros abrangem, e vem do back, com tres valores:
 *
 *   'processo'  ha fila drenando; o recorte comeca na carta viva mais antiga
 *   'filtrado'  a operadora pediu um recorte pelos filtros da tela de fila
 *   'vazio'     sem fila e sem filtro — os totais sao ZERO, nao o acumulado do evento
 *
 * Os rotulos seguem os tres. Tratar so 'processo' e mandar o resto para um texto de
 * "no evento" fazia o card afirmar o que o numero nao e: com filtro, "126 enviadas no
 * evento, desde sempre" num evento que ja mandou 512.
 */
/**
 * Quanto tempo ate a fila entregar `quantidade` cartas, no ritmo que o back informou.
 *
 * `ritmo` vem de `progresso` — `porCiclo` e config de servidor e `intervaloSegundos` e a
 * copia do agendamento do n8n. Sem ele devolve null, e quem chama nao mostra nada: numero
 * de previsao errado e pior do que previsao nenhuma, porque o operador so descobre
 * conferindo no relogio.
 *
 * Sempre com "~". O ciclo pode encontrar linha abandonada para devolver a fila, e carta que
 * falha consome a vaga sem sair. Serve para decidir entre esperar e forcar, nao para marcar
 * reuniao.
 */
export function estimativaFila(quantidade, ritmo) {
    const porCiclo = (ritmo && ritmo.porCiclo) || 0;
    const intervalo = (ritmo && ritmo.intervaloSegundos) || 0;

    if (!quantidade || quantidade <= 0 || porCiclo <= 0 || intervalo <= 0) return null;

    const minutos = Math.ceil((Math.ceil(quantidade / porCiclo) * intervalo) / 60);

    if (minutos < 60) return `~${minutos} min`;

    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    return resto === 0 ? `~${horas} h` : `~${horas} h ${resto} min`;
}

// A escolha do "Mostrar mais/menos" da rosca, em cookie para valer nas proximas visitas e nas
// duas filas (do evento e de todos os eventos). Sem cookie — primeira visita, apagado ou
// bloqueado — o painel nasce aberto.
const COOKIE_ROSCA_ABERTA = 'cartaFilaRoscaAberta';

function lerRoscaAberta() {
    try {
        const par = document.cookie.split('; ').find((c) => c.startsWith(`${COOKIE_ROSCA_ABERTA}=`));
        return par ? par.slice(COOKIE_ROSCA_ABERTA.length + 1) !== '0' : true;
    } catch (e) {
        return true;
    }
}

function gravarRoscaAberta(aberta) {
    try {
        document.cookie = `${COOKIE_ROSCA_ABERTA}=${aberta ? '1' : '0'}; path=/; max-age=31536000; SameSite=Lax`;
    } catch (e) {
        // Cookie bloqueado: a escolha vale so enquanto a tela estiver aberta.
    }
}

/**
 * A mesma contagem dos cards, em proporcao, e as proximas cartas a sair.
 *
 * Nasce aberta: as proximas da fila nao aparecem em card nenhum, e fechadas atras do "Mostrar
 * mais" ficavam escondidas de quem as procura. O operador pode recolher, e a escolha fica em
 * cookie (lerRoscaAberta). O que a rosca acrescenta aos cards e a PROPORCAO, que numero nenhum
 * da — por isso o centro mostra a % concluida, e nao o total, que ja tem card proprio. Se um dia
 * o centro virar o total, a rosca perde a razao de existir e deve sair.
 *
 * `proximas` responde QUEM sai primeiro e quando, que e o que o operador procura quando
 * alguem pergunta pela propria carta. Posicao na fila global, previsao pelo mesmo `ritmo`
 * dos cards.
 *
 * `mostrarEvento` e da fila de todos os eventos: la a mesma pessoa pode estar na fila de dois
 * eventos, e o nome sozinho nao diz qual carta e qual.
 */
export function RoscaCartaFila({ aguardando = 0, enviadas = 0, falhas = 0, proximas = [], ritmo, mostrarEvento = false }) {
    const [aberta, setAberta] = useState(lerRoscaAberta);

    // Grava so quando o operador troca. Sem escolha feita, vale o padrao: aberta.
    const alternar = () => {
        const nova = !aberta;
        setAberta(nova);
        gravarRoscaAberta(nova);
    };

    const total = aguardando + enviadas + falhas;

    if (total <= 0) return null;

    // Raio e perimetro do anel. O vao entre fatias nao e enfeite: aguardando e enviadas tem
    // luminancia parecida, e encostadas leem como uma mancha so.
    const R = 48;
    const C = 2 * Math.PI * R;
    const VAO = 3;

    const arco = (quantidade) => Math.max(0, (quantidade / total) * C - VAO);
    const antes = (quantidade) => (quantidade / total) * C;

    const pct = (quantidade) => Math.round((quantidade / total) * 100);
    const concluido = pct(enviadas);

    const fatias = [
        { cor: 'var(--fin-ok-g)', rotulo: 'Enviadas',   valor: enviadas,   deslocamento: 0 },
        { cor: 'var(--fin-wn-g)', rotulo: 'Aguardando', valor: aguardando, deslocamento: antes(enviadas) },
        { cor: 'var(--fin-dg-g)', rotulo: 'Falhas',     valor: falhas,     deslocamento: antes(enviadas + aguardando) },
    ];

    // Fechada, so o rotulo, fora do card. Aberta, o "Mostrar menos" vai para dentro dele.
    if (!aberta) {
        return (
            <div className="fin-pp__disc">
                <button
                    type="button"
                    className="fin-pp__disc-btn"
                    aria-expanded={false}
                    aria-controls="rosca-carta-fila"
                    onClick={alternar}>
                    Mostrar mais
                    <span className="fin-pp__disc-ar" aria-hidden="true">{'\u2193'}</span>
                </button>
            </div>
        );
    }

    return (
        <div className="fin-pp__rosca" id="rosca-carta-fila">
            <svg
                viewBox="0 0 120 120"
                width="100%"
                height="132"
                role="img"
                aria-label={`${enviadas} enviadas, ${aguardando} aguardando, ${falhas} falhas, de ${total}. ${concluido}% concluído.`}>
                <circle cx="60" cy="60" r={R} fill="none" stroke="var(--fin-sf2)" strokeWidth="14" />
                <g transform="rotate(-90 60 60)" fill="none" strokeWidth="14">
                    {fatias.map((f) => (f.valor > 0 ? (
                        <circle
                            key={f.rotulo}
                            cx="60"
                            cy="60"
                            r={R}
                            stroke={f.cor}
                            strokeDasharray={`${arco(f.valor)} ${C}`}
                            strokeDashoffset={-f.deslocamento} />
                    ) : null))}
                </g>
                <text x="60" y="57" textAnchor="middle" fontSize="22" fontWeight="700" fill="var(--fin-ac)">
                    {concluido}%
                </text>
                <text x="60" y="74" textAnchor="middle" fontSize="9" fill="var(--fin-ft)">concluído</text>
            </svg>

            <div className="fin-pp__rosca-leg">
                {fatias.map((f) => (
                    <div className="fin-pp__rosca-row" key={f.rotulo}>
                        <i className="fin-pp__rosca-sw" style={{ background: f.cor }} />
                        {f.rotulo}
                        <b>{f.valor}</b>
                        <span className="fin-pp__rosca-pct">{pct(f.valor)}%</span>
                    </div>
                ))}
            </div>

            <div className="fin-pp__rosca-prox">
                {/* O "Mostrar menos" fica no topo da coluna da direita, que e o canto superior
                    direito do card: onde estava o "Mostrar mais", e sem cobrir numero nenhum. */}
                <div className="fin-pp__rosca-prox-head">
                    <span className="fin-pp__kpi-label">Próximas na fila</span>
                    <button
                        type="button"
                        className="fin-pp__disc-btn"
                        aria-expanded={true}
                        aria-controls="rosca-carta-fila"
                        style={{ transform: 'translateY(-4px)' }}
                        onClick={alternar}>
                        Mostrar menos
                        <span className="fin-pp__disc-ar" aria-hidden="true">{'\u2191'}</span>
                    </button>
                </div>
                {proximas.length === 0 ? (
                    <span className="fin-pp__rosca-prox-vazio">Nenhuma carta aguardando envio</span>
                ) : proximas.map((c) => (
                    <div className="fin-pp__rosca-prox-row" key={c.id}>
                        <span className="fin-pp__rosca-prox-pos">{c.posicao ? `${c.posicao}º` : '—'}</span>
                        <span
                            className="fin-pp__rosca-prox-nome"
                            title={mostrarEvento && c.evento_nome
                                ? `${c.participante_nome || c.participante} · ${c.evento_nome}`
                                : (c.participante_nome || String(c.participante))}>
                            {c.participante_nome || c.participante}
                            {mostrarEvento && c.evento_nome ? ` · ${c.evento_nome}` : ''}
                        </span>
                        <span className="fin-pp__minitag">
                            <RiMailSendLine size={11} /> {ROTULO_TIPO[c.tipo] || c.tipo}
                        </span>
                        {/* Carta ja reivindicada pelo ciclo nao tem mais "quando": ela esta
                            saindo. Mostrar ~1 min ali diria que ainda falta um ciclo. */}
                        {c.status === 'processando' ? (
                            <span className="fin-pp__rosca-prox-quando is-info">saindo agora</span>
                        ) : (
                            <span className="fin-pp__rosca-prox-quando">
                                {estimativaFila(c.posicao, ritmo) ? `em ${estimativaFila(c.posicao, ritmo)}` : 'sem previsão'}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function CardsCartaFila({ totais, idEvento, escopo = 'vazio', falhasNoProcesso = 0, aoEscolherStatus, visaoAtiva = '', compacto = true, ritmo, naFrente = 0, eventosNaFrente = 0, comecou = false, dicaClique = 'Ver na tabela abaixo' }) {
    const t = totais || {};
    const pendentes = t.pendente || 0;
    const processando = t.processando || 0;
    const enviadas = t.enviado || 0;
    const falhas = t.falhou || 0;
    // No processo, a falha que conta para o total e a deste envio — `falhas` traz tambem a
    // antiga, que o card de Falhas mostra de proposito mas nao pertence a este disparo.
    const total = pendentes + processando + enviadas
        + (escopo === 'processo' ? falhasNoProcesso : falhas);

    // Clique no card filtra a tabela por aquele status. So a tela da fila passa o callback:
    // no painel de participantes nao ha tabela para filtrar, e um card que parece clicavel
    // e nao faz nada e pior do que um card estatico.
    //
    // `div` com role e onKeyDown, e nao `button`: o card ja tem estrutura interna com
    // varios elementos e trocar a tag quebraria o layout do `.fin-pp__kpi` — mas sem o
    // teclado ele ficaria inalcancavel para quem nao usa mouse.
    // A visao '' e a de falhas — e o estado inicial da tela. Sem trata-la junto de
    // 'falhou', a tabela abre mostrando falhas e nenhum card aparece marcado, e o
    // operador le isso como "nenhum filtro", que e o oposto do que esta vendo.
    const ativo = (status) => (status === 'falhou' ? (visaoAtiva === 'falhou' || visaoAtiva === '') : visaoAtiva === status);

    // Duas perguntas diferentes, e qual delas interessa depende de o disparo ter comecado.
    //
    // Antes da primeira carta sair, o que angustia e "quando comeca" — e a resposta esta no
    // que ha na FRENTE, de todos os eventos, porque a fila e uma so. Depois que comecou, o
    // que se quer saber e "quando acaba", e ai entra o que falta desta fila tambem.
    //
    // Dizer sempre o tempo total seria pior nos dois casos: quem ainda nao comecou leria um
    // numero que inclui trabalho que nem e dele, e quem ja esta no meio leria o tempo de uma
    // espera que ja passou.
    const previsao = (() => {
        const naFila = pendentes + processando;

        if (naFila <= 0) return null;

        if (comecou) {
            const t = estimativaFila(naFrente + naFila, ritmo);
            return t ? `termina em ${t}` : null;
        }

        // Nada na frente: entra no proximo ciclo. Nao e "~0 min", que leria como "ja saiu".
        if (naFrente <= 0) return 'começa no próximo ciclo';

        const t = estimativaFila(naFrente, ritmo);
        return t ? `começa em ${t}` : null;
    })();

    const clicavel = (status) => (!aoEscolherStatus ? {} : {
        role: 'button',
        tabIndex: 0,
        className: `fin-pp__kpi is-clickable${ativo(status) ? ' is-active' : ''}`,
        // Sem recorte em vigor (visaoAtiva null, o painel), o card leva a outra tela e nao liga
        // nem desliga nada: aria-pressed ali anunciaria um botao de alternar que nao existe.
        'aria-pressed': visaoAtiva === null ? undefined : ativo(status),
        title: dicaClique,
        onClick: () => aoEscolherStatus(status),
        onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                aoEscolherStatus(status);
            }
        },
    });

    const resumo = (
        <>
            <div className="fin-pp__kpi-top">
                <span className="fin-pp__kpi-label">{ROTULO_TOTAL[escopo] || ROTULO_TOTAL.vazio}</span>
                <span className="fin-pp__kpi-ic is-info"><RiTimeLine size={16} /></span>
            </div>
            <div className="fin-pp__kpi-value is-info">{total}</div>
            <div className="fin-pp__kpi-sub">
                {SUB_TOTAL[escopo] || SUB_TOTAL.vazio}
            </div>
        </>
    );

    return (
        // Compacto onde os cards sao apoio (painel de participantes); tamanho cheio na tela
        // de processo, onde o resultado do lote e o assunto. Os dois modificadores dao a mesma
        // grade de 4 colunas — so a densidade muda. Padrao compacto para o painel, que e o
        // uso que ja existia, continuar igual sem passar nada.
        <div className={`fin-pp__kpis ${compacto ? 'fin-pp__kpis--compact' : 'fin-pp__kpis--4col'}`} style={{ marginBottom: 16 }}>

            <div className="fin-pp__kpi" {...clicavel('pendente')}>
                <div className="fin-pp__kpi-top">
                    <span className="fin-pp__kpi-label">Aguardando envio</span>
                    <span className={`fin-pp__kpi-ic ${pendentes > 0 ? 'is-warn' : ''}`}><RiMailSendLine size={16} /></span>
                </div>
                <div className={`fin-pp__kpi-value ${pendentes > 0 ? 'is-warn' : ''}`}>{pendentes}</div>
                <div className="fin-pp__kpi-sub">
                    {processando > 0
                        ? `${processando} saindo agora`
                        : (pendentes > 0 ? 'na fila' : 'fila vazia')}
                    {previsao ? <> · <strong>{previsao}</strong></> : null}
                </div>
                {/* De onde vem a espera. Sem isto o operador le "comeca ~40 min" e conclui
                    que o sistema travou; com isto ele ve que ha trabalho legitimo na frente,
                    e para de abrir chamado. So enquanto nao comecou — depois que a primeira
                    carta sai, o que esta atras dele nao muda mais o numero dele. */}
                {!comecou && naFrente > 0 ? (
                    <div className="fin-pp__kpi-sub">
                        {naFrente} {naFrente === 1 ? 'carta' : 'cartas'} na frente
                        {eventosNaFrente > 0
                            ? `, de ${eventosNaFrente} ${eventosNaFrente === 1 ? 'evento' : 'eventos'}`
                            : ''}
                    </div>
                ) : null}
            </div>

            <div className="fin-pp__kpi" {...clicavel('enviado')}>
                <div className="fin-pp__kpi-top">
                    <span className="fin-pp__kpi-label">Enviadas</span>
                    <span className="fin-pp__kpi-ic is-ok"><RiCheckboxCircleLine size={16} /></span>
                </div>
                <div className="fin-pp__kpi-value is-ok">{enviadas}</div>
                <div className="fin-pp__kpi-sub">{SUB_ENVIADAS[escopo] || SUB_ENVIADAS.vazio}</div>
            </div>

            <div className="fin-pp__kpi" {...clicavel('falhou')}>
                <div className="fin-pp__kpi-top">
                    <span className="fin-pp__kpi-label">Falhas</span>
                    <span className={`fin-pp__kpi-ic ${falhas > 0 ? 'is-danger' : ''}`}><RiErrorWarningLine size={16} /></span>
                </div>
                <div className={`fin-pp__kpi-value ${falhas > 0 ? 'is-danger' : ''}`}>{falhas}</div>
                <div className="fin-pp__kpi-sub">
                    {falhas === 0
                        ? (SUB_SEM_FALHA[escopo] || SUB_SEM_FALHA.vazio)
                        : (idEvento
                            // No painel nao ha tabela de falhas: o motivo so existe na tela
                            // de processo, e o quarto card e o caminho ate ela.
                            ? 'veja o motivo na fila de envio'
                            : 'motivo na tabela abaixo')}
                </div>
            </div>

            {idEvento ? (
                <Link to={`/cartaFila/${idEvento}`} className="fin-pp__kpi fin-pp__kpi--link">
                    {resumo}
                    <span className="fin-pp__kpi-link-cta">Acompanhar a fila <RiArrowRightLine size={14} /></span>
                </Link>
            ) : (
                <div className="fin-pp__kpi" {...clicavel('todos')}>{resumo}</div>
            )}

        </div>
    );
}

/**
 * Os mesmos cards no painel de participantes, e so enquanto ha carta na fila.
 *
 * Somem quando a fila esvazia — e o sinal de que o disparo terminou, e evita que o
 * painel fique com um aviso permanente que ninguem mais le. Na tela de processo eles
 * continuam visiveis, porque la o resultado do lote e o assunto da tela.
 */
export function CardCartaFila({ fila, idEvento }) {
    // Antes do retorno antecipado: hook nao pode ficar condicionado.
    const history = useHistory();

    if (!fila || !fila.emAndamento) return null;

    // No painel nao ha tabela da fila para filtrar. O clique no card abre a tela de processo ja
    // no recorte dele — o mesmo gesto da tela de processo, com o destino que existe aqui.
    const abrirFila = (status) => history.push({ pathname: `/cartaFila/${idEvento}`, search: `?visao=${status}` });

    // visaoAtiva null: nenhum recorte escolhido no painel. Com o padrao '' o card de Falhas
    // nasceria marcado, porque a visao inicial da tela de processo e a de falhas.
    return (
        <CardsCartaFila
            totais={fila.totais}
            idEvento={idEvento}
            escopo={fila.escopo}
            falhasNoProcesso={fila.falhasNoProcesso}
            ritmo={fila.ritmo}
            naFrente={fila.naFrente}
            eventosNaFrente={fila.eventosNaFrente}
            comecou={fila.comecou}
            aoEscolherStatus={abrirFila}
            visaoAtiva={null}
            dicaClique="Ver na fila de envio" />
    );
}
